import { BEARER_TOKEN } from "../../config";

export const upsertMapDraft = async (baseUrl, payload) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  if (!payload || typeof payload !== "object") throw new Error("Missing payload");

  const response = await fetch(`${baseUrl}/api/mgmt/farm/map-draft/upsert`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  return response;
};

export const getMapDraft = async (baseUrl, { orgId, farmId, phoneNumber } = {}) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  const url = new URL(`${baseUrl}/api/mgmt/farm/map-draft/get`);
  url.searchParams.set("orgId", String(orgId ?? 0));
  url.searchParams.set("farmId", String(farmId ?? 0));
  if (phoneNumber != null && String(phoneNumber).trim()) {
    url.searchParams.set("phoneNumber", String(phoneNumber).trim());
  }

  const response = await fetch(url.toString(), {
    method: "GET",
  });

  return response;
};

export const uploadFarmImage = (baseUrl, file, { onProgress } = {}) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  if (!file) throw new Error("Missing file");

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${baseUrl}/api/public/farm-images/upload`);

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const pct = Math.round((evt.loaded / evt.total) * 100);
      if (typeof onProgress === "function") onProgress(pct);
    };

    xhr.onload = () => {
      const text = xhr.responseText || "";
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (_) {
        json = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve({ status: xhr.status, json, text });
      else reject(new Error(json?.message || json?.code || text || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed (network error)"));

    const form = new FormData();
    form.append("images", file);
    xhr.send(form);
  });
};

export const deleteFarmImage = async (baseUrl, url) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  const u = String(url || "").trim();
  if (!u) throw new Error("Missing url");

  const response = await fetch(`${baseUrl}/api/public/farm-images/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url: u }),
  });

  return response;
};

export const getFarmDesign = async (baseUrl, { orgId, farmId, phoneNumber } = {}) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");

  const url = new URL(`${baseUrl}/api/mgmt/farm/design/get`);
  const o = Number(orgId ?? 0);
  const f = Number(farmId ?? 0);

  if (Number.isFinite(o) && Number.isFinite(f) && (o !== 0 || f !== 0)) {
    url.searchParams.set("orgId", String(o));
    url.searchParams.set("farmId", String(f));
  } else if (phoneNumber != null && String(phoneNumber).trim()) {
    url.searchParams.set("phoneNumber", String(phoneNumber).trim());
  } else {
    throw new Error("Missing orgId/farmId or phoneNumber");
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: "Bearer " + BEARER_TOKEN,
    },
  });

  return response;
};

export const listAllMapDraftsByType = async (baseUrl, { limit = 15, offset = 0, type = "guest", sourceOrgId } = {}) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  const url = new URL(`${baseUrl}/api/mgmt/farm/map-draft/list-all`);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("type", type);
  if (sourceOrgId != null) {
    url.searchParams.set("sourceOrgId", String(sourceOrgId));
  }

  const response = await fetch(url.toString(), { method: "GET" });
  return response;
};

export const searchMapDrafts = async (baseUrl, { orgId, farmId, phoneNumber, sourceOrgId } = {}) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  const url = new URL(`${baseUrl}/api/mgmt/farm/map-draft/search`);
  if (orgId != null && farmId != null) {
    url.searchParams.set("orgId", String(orgId));
    url.searchParams.set("farmId", String(farmId));
  }
  if (phoneNumber != null && String(phoneNumber).trim()) {
    url.searchParams.set("phoneNumber", String(phoneNumber).trim());
  }
  if (sourceOrgId != null) {
    url.searchParams.set("sourceOrgId", String(sourceOrgId));
  }

  const response = await fetch(url.toString(), { method: "GET" });
  return response;
};

// Reference data for the design form — every engine knob (soil texture,
// crop, emitter type, ...) with its default and label, and the ~26 area
// units the engine accepts. Fetch once and build the form from this instead
// of hardcoding a list that can drift from what the engine actually knows.
export const getDesignOptions = async (baseUrl) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");

  const response = await fetch(`${baseUrl}/api/mgmt/farm/design/options`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + BEARER_TOKEN,
    },
  });

  return response;
};

export const getDesignUnits = async (baseUrl) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");

  const response = await fetch(`${baseUrl}/api/mgmt/farm/design/units`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + BEARER_TOKEN,
    },
  });

  return response;
};

export const upsertFarmDesign = async (baseUrl, payload) => {
  if (!baseUrl) throw new Error("Missing REACT_APP_BASE_URL");
  if (!payload || typeof payload !== "object") throw new Error("Missing payload");

  const response = await fetch(`${baseUrl}/api/mgmt/farm/design/upsert`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + BEARER_TOKEN,
    },
    body: JSON.stringify(payload),
  });

  return response;
};
